/* eslint-disable react/prop-types */

/**
 * Special onEnter function to redirect all
 * of the external routes.
 */
import React from 'react'
import { Redirect } from 'react-router-dom'
import { batch } from 'react-redux' // eslint-disable-line no-restricted-imports
import * as ViewActions from '../actions/Actions'
import store from '../stores/configureStore'
import * as SendActions from '../actions/send'
import Events from 'lib/analytics/eventTags'
import { amountIsZero } from 'lib/amountUtils'
import { parseSearch, safeParseWindowSearch } from './url-utils'
import {
  ANALYTICS_FLOW_TYPE,
  ENV_TYPE,
  PAYMENT_TYPE,
} from '../../../../lib/constants'
import { OPEN_BANKING_FLOWS } from '../constants'
import serverProps from './serverProps'
import logger from 'loglevel'
import { isOpenBankingTreatment } from '../../experiments/openBanking'
import { tracking } from '../../lib/analytics/mParticle'
import {
  trackServiceAndClientDecision,
  togglePhoneConfirmation,
} from '../actions/unified-actions'
import MGMLandingPage from '../components/mgm/mgmLandingPage'
import { mgmValidateRoute } from './mgm-redirect'
import { getMainWindow } from '../../lib/iframeUtils'

const flowMap = {
  [PAYMENT_TYPE.PERSONAL]: 'send',
  [PAYMENT_TYPE.PURCHASE]: 'buy',
  [PAYMENT_TYPE.DONATION]: 'buy',
}

export default function onEnter(props) {
  let { env, type } = props.match.params
  if (props.match.url.includes('payRequest')) {
    env = ENV_TYPE.PAY_REQUEST
  }
  const state = store.getState()
  const {
    recipientType,
    fundingOptionsData,
    obFlow,
    analyticsFlow,
    flowType,
  } = serverProps
  let {
    server: {
      rootURL,
      groupFeatures: { shouldShowSelectFundingOptionsPage = false },
    },
    recipient: {
      suggestedPaymentType,
      isServiceSupportedExternalFlow,
      cipFlow,
      serviceContext,
    },
    transaction: { paymentTypeIsSelected } = {},
  } = state

  // Flow type is restored from session for non-initial external entry points.
  // We must preserve it even if suggestedPaymentType is defined, except when
  // :type URL param exists for '/:type/external/:env' which takes precedence.
  if (!type && flowType) {
    type = flowType
  }

  // capture external params from url
  const mainWindow = getMainWindow()
  const params = {
    ...safeParseWindowSearch(mainWindow),
    ...parseSearch(props.location.search),
    env,
    type,
    cipFlow,
  }
  const {
    success: base64SuccessfullyAddedFundingInstrumentIds = '',
    error: base64ErrorFlow = '',
    otpSuccess,
  } = params

  /**
   *Handle the rendering of MGM Landing Page in External Flow
   * if showPreview = y, then preview page is rendered for MGM
   * Else if there is no showPreview, then MGM landing page is shown
   */
  if (env === ENV_TYPE.MGM && params.showPreview !== 'y') {
    return mgmValidateRoute(props) || <MGMLandingPage />
  }

  const isOpenBankingReconsentFlow =
    isOpenBankingTreatment() &&
    obFlow === OPEN_BANKING_FLOWS.CONSENT_CONTINGENCY

  let successfullyAddedFundingInstrumentIds = ''
  if (
    base64SuccessfullyAddedFundingInstrumentIds &&
    /**
     * Some external flows (such as Negative Balance) use the success parameter
     * as a string value boolean. But the success flag is used to autoselect
     * a funding instrument on a plan for when resolving contingencies.
     *
     * As there is no future-proof & scalable way to detect for valid funding
     * instrument ID, we need to fall back to a list of omited values.
     *
     * dHJ1ZQ = true
     *
     * If we need to add more, simply: /dHJ1ZQ|whatever|etc/
     */
    !/dHJ1ZQ/.test(base64SuccessfullyAddedFundingInstrumentIds)
  ) {
    try {
      // window.atob() method decodes a base-64 encoded string.
      successfullyAddedFundingInstrumentIds = atob(
        base64SuccessfullyAddedFundingInstrumentIds
      )
    } catch (e) {
      // There was an error parsing the FI id returned by wallet
      // If this happen, we continue with the regular flow because we can not auto select that FI
      logger.error(e)
    }
  }

  tracking(
    Events.p2p_external_entry_occurred({
      flow_from: env || 'unknown',
      http_params: JSON.stringify({
        ...params,
        ...(obFlow && { obFlow }),
        ...(successfullyAddedFundingInstrumentIds && {
          fi_id: successfullyAddedFundingInstrumentIds,
        }),
      }),
    })
  )

  trackServiceAndClientDecision({ serviceContext, suggestedPaymentType })

  // url honoring of type
  if (
    analyticsFlow !== ANALYTICS_FLOW_TYPE.ACTIVITY &&
    type &&
    !isServiceSupportedExternalFlow
  ) {
    if (type === 'send') {
      suggestedPaymentType = PAYMENT_TYPE.PERSONAL
    } else if (type === 'buy') {
      suggestedPaymentType = PAYMENT_TYPE.PURCHASE
    }
  }

  // Moved this logic from old external-redirect file for ppme.
  // This takes precedence over the url honoring
  if (
    env === ENV_TYPE.PP_ME &&
    recipientType === 'BUSINESS' &&
    type === 'send'
  ) {
    suggestedPaymentType = PAYMENT_TYPE.PURCHASE
  }
  // some apps send data via query params
  if (cipFlow || state.external.captureQueryParams) {
    store.dispatch(ViewActions.captureExternalParams(params))
  }

  /**
   * Created a separate Action to capture environment specific params
   * after the redirection is complete and flow returns back to p2p external flow
   */
  store.dispatch(ViewActions.captureExternalIntegrationParams(params))

  /**
   * This is specifically to handle the url params for phone confirmation flow.
   * We can't have an env=phoneconfirmation as it would override other environments.
   */
  if (otpSuccess !== undefined) {
    store.dispatch(togglePhoneConfirmation(params))
  }

  // trigger the external initialize hook
  store.dispatch(ViewActions.triggerExternalHook('initialize', state))
  // treat DONATION as PURCHASE so that we don't break existing tracking by this change.
  if (!paymentTypeIsSelected) {
    store.dispatch(
      ViewActions.changeIsPurchase(
        [PAYMENT_TYPE.PURCHASE, PAYMENT_TYPE.DONATION].includes(
          suggestedPaymentType
        )
      )
    )
  }

  if (isServiceSupportedExternalFlow) {
    store.dispatch(
      ViewActions.setServiceReturnedPaymentType(suggestedPaymentType)
    )
  }

  if (successfullyAddedFundingInstrumentIds) {
    store.dispatch(
      SendActions.saveSuccessfullyAddedFundingInstrumentIds(
        successfullyAddedFundingInstrumentIds
      )
    )
  }

  if (isOpenBankingReconsentFlow) {
    const ignorePreferred = true
    batch(() => {
      if (base64ErrorFlow) {
        store.dispatch(SendActions.setOpenBankingError(base64ErrorFlow))
      }
      store.dispatch(
        SendActions.receivedFundingOptions(
          fundingOptionsData,
          shouldShowSelectFundingOptionsPage,
          ignorePreferred
        )
      )
    })
  } else if (
    // do not fetch funding options for payRequest & hawk-ppcp
    env !== ENV_TYPE.HAWK_PPCP &&
    env !== ENV_TYPE.PAY_REQUEST &&
    !amountIsZero(store.getState().amount.amount) &&
    (state.external.action !== 'request' || state.external.action !== 'split')
  ) {
    let instrumentIds = []
    if (
      successfullyAddedFundingInstrumentIds &&
      typeof successfullyAddedFundingInstrumentIds === 'string'
    ) {
      instrumentIds = successfullyAddedFundingInstrumentIds.split(',')
    }
    // immediately start looking for funding options if not request route
    // checking amount because fetchFundingOptions requires valid amount
    // NOTE: use store.getState() here to get the *latest* amount
    // Update: Do not fetch funding option here if it's 'hawk-ppcp' case, since it's an external route without knowing payment type
    // FI will be fetched after user choose payment type
    store.dispatch(SendActions.fetchFundingOptions({ instrumentIds }))
  }

  if (env === ENV_TYPE.PAY_REQUEST) {
    const { requestId, requestLegId } = props.match.params
    return (
      <Redirect to={`${rootURL}/payRequest/${requestId}/${requestLegId}`} />
    )
  }

  if (
    suggestedPaymentType === PAYMENT_TYPE.PURCHASE ||
    suggestedPaymentType === PAYMENT_TYPE.PERSONAL ||
    suggestedPaymentType === PAYMENT_TYPE.DONATION
  ) {
    if (env === ENV_TYPE.CROSS_BORDER) {
      return (
        <Redirect to={`${rootURL}/${flowMap[suggestedPaymentType]}/preview`} />
      )
    }

    return (
      <Redirect
        to={`${rootURL}/homepage/${flowMap[suggestedPaymentType]}/preview`}
      />
    )
  }

  // otherwise go to preview page but ask to show the payment type overpanel first
  return <Redirect to={`${rootURL}/homepage/preview`} />
}
